/**
 * Creating a new instance of JWTAuthService
 * @class JWTAuthService
 * @description
 * This is auth service, responsible for managing the Authentication related task.
 */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { APP_USER, AUTH_PREFIX } from '@configs/app-settings.config';
import { CommonBase } from '@core/interfaces/common-base';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoggerService } from './logger.service';
import { getToken } from '../helpers/utils';
import { appRoutes } from '@app/configs/route-paths-const.config';
import { subscriptionStatusEnum } from '@app/modules/shared/models/user-plan-detail.model';
import { UserModel } from '@app/modules/shared/models/user.model';

@Injectable({
  providedIn: 'root'
})
export class JWTAuthService {
  constructor(
    private router: Router,
    public jwtHelper: JwtHelperService,
    private readonly loggerService: LoggerService
  ) { }


  /**
   * @function logout
   * @description
   * Check current login user token exists
   * @returns {Observable<any>}
   */
  logout(): Observable<boolean> {
    this.deleteUserAccessToken();
    return observableOf(true);
  }

  /*** @function getUserInfo
   * @description
   * Check current login user token exists
   * @returns {Observable<any>}
   */
  getUserInfo(): Observable<UserModel> {
    const savedCredentials = this.getLoginUser();
    let user = this.decodeJwt(savedCredentials.authToken);
    return observableOf(user);
  }

  /*** @function IsAuthUser
   * @description
   * Check current login user token exists
   * @returns {boolean}
   */
  IsAuthUser(): boolean {
    let res: boolean = false;
    let token = null;
    try {
      token = getToken();
      if (token) {
        res = !this.jwtHelper.isTokenExpired(token);
      }
    } catch (error) {
      res = false;
    }
    return res;
  }

  /*** @function setLoginUserDetail
  * @description
  * Set login user details in local storage
  * @param {obj} {CommonBase}
  * @returns none
  */
  setLoginUserDetail(obj: CommonBase): void {
    window.localStorage[APP_USER] = JSON.stringify(obj);
  }

  /*** @function
  * @description Set login user details in local storage
  * @param {data} CommonBase
  * @returns none
  */
  updateLoginUserDetail(data: CommonBase): void {
    if (window.localStorage[APP_USER]) {
      let authObject = JSON.parse(window.localStorage[APP_USER]);
      if (authObject && data) {
        authObject.fullName = data.fullName;
      }
      window.localStorage[APP_USER] = JSON.stringify(authObject);
    }
  }

  /*** @function getUserAccessToken
   * @description
   * Get user access token
   * @returns null | CommonBase
  */
  getUserAccessToken(): null | CommonBase {
    if (this.IsAuthUser()) {
      return JSON.parse(window.localStorage[APP_USER]).authToken;
    } else {
      return null;
    }
  }

  /*** @function deleteUserAccessToken
  * @param {redirect} boolean
  * @description
  * Delete user access token
  * @returns {void}
  */
  deleteUserAccessToken(redirect: boolean = true): void {
    //window.localStorage.clear();
    this.clearLocalStorage();
    if (redirect === true) {
      this.router.navigate([appRoutes.login]);
    }
  }

  /**
   * clear the local storage
   */
  async clearLocalStorage() {
    try {
      for (var i in localStorage) {
        if (!i.includes('CB_APP_recQualityRemindMe')) {
          localStorage.removeItem(i);
        }
      }
    } catch (error) {
    }
  }


  /*** @function getLoginUserId
  * @description
  * Get login user id
  * @returns {CommonBase}
  */
  getLoginUserId(): CommonBase {
    if (window.localStorage[APP_USER]) {
      return JSON.parse(window.localStorage[APP_USER]).User_Id;
    }
  }

  /*** @function getLoginUserEmail
  * @description
  * Get login user email
  * @returns {CommonBase}
  */
  getLoginUserEmail(): CommonBase {
    if (window.localStorage[APP_USER]) {
      return JSON.parse(window.localStorage[APP_USER]).Email;
    }
  }

  /*** @function getLoginUserNameLetter
  * @description
  * Get login user name first letter
  * @returns {CommonBase}
  */
  getLoginUserNameLetter(): CommonBase {
    if (window.localStorage[APP_USER]) {
      return JSON.parse(window.localStorage[APP_USER]).Name.substring(0, 1);
    }
  }

  /*** @function getLoginUser
  * @description
  * Get login user details from local storage
  * @returns {CommonBase}
  */
  private getLoginUser(): CommonBase {
    const savedCredentials = localStorage.getItem(APP_USER);
    return JSON.parse(savedCredentials);
  }
  /**
   * decode token
   * @param token 
   */
  decodeJwt(token: string) {
    var tokenPayload = this.jwtHelper.decodeToken(token);
    return tokenPayload
  }
  /**
   * check user has a subscription plan 
   */
  getUserSubscription(): subscriptionStatusEnum {
    let token = getToken();
    let payload = this.decodeJwt(token);
    return (payload && payload.subscriptionStatus) ? payload.subscriptionStatus : 'none';
  }

}
